import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Anchor } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoRideau from '@/assets/logo-rideau.png';
import type { LegalPageType } from '@/components/LegalModal';
const LegalModal = lazy(() => import('@/components/LegalModal'));
import CookieBanner from '@/components/CookieBanner';

const Footer = () => {
  const { t } = useTranslation();
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalPageType, setLegalPageType] = useState<LegalPageType>(null);
  const [cookieBannerOpen, setCookieBannerOpen] = useState(false);

  const openLegalModal = (type: LegalPageType) => {
    setLegalPageType(type);
    setLegalModalOpen(true);
  };

  const handleOpenCookiePolicy = () => {
    openLegalModal('cookies');
  };

  const handleManageCookies = () => {
    setCookieBannerOpen(true);
  };

  return (
    <>
      <footer className="bg-primary border-t border-border">
        <div className="container-premium py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img
                  src={logoRideau}
                  alt="SAS RIDEAU"
                  className="h-14 w-14 rounded-full object-cover"
                />
                <span className="font-serif text-xl font-semibold text-foreground">
                  SAS RIDEAU
                </span>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('footer.tagline')}
              </p>
              <div className="flex items-center gap-2 text-accent">
                <Anchor size={18} />
                <span className="text-sm font-medium">{t('footer.since')}</span>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
                {t('footer.navigation')}
              </h4>
              <ul className="space-y-3">
                {[
                  { name: t('footer.navLinks.home'), href: '/' },
                  { name: t('footer.navLinks.marketAccess'), href: '/partenaires' },
                  { name: t('footer.navLinks.company'), href: '/societe' },
                  { name: t('footer.navLinks.products'), href: '/produits' },
                  { name: t('footer.navLinks.contact'), href: '/contact' },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="text-muted-foreground hover:text-accent transition-colors text-sm"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Produits */}
            <div>
              <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
                {t('footer.ourProducts')}
              </h4>
              <ul className="space-y-3">
                {[
                  { name: t('footer.productLinks.langoustine'), href: '/produits#langoustine' },
                  { name: t('footer.productLinks.lobster'), href: '/produits#homard' },
                  { name: t('footer.productLinks.scallops'), href: '/produits#saint-jacques' },
                  { name: t('footer.productLinks.salmon'), href: '/produits#saumon' },
                  { name: t('footer.productLinks.razorclams'), href: '/produits#couteaux' },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="text-muted-foreground hover:text-accent transition-colors text-sm"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-serif text-lg font-semibold text-foreground mb-6">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">
                    France
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-accent flex-shrink-0" />
                <a href="tel:+33456190053" className="text-muted-foreground hover:text-accent text-sm">
                    +33 4 56 19 00 53
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-accent flex-shrink-0" />
                  <a href="mailto:info@societerideau.fr" className="text-muted-foreground hover:text-accent text-sm">
                    info@societerideau.fr
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              © {new Date().getFullYear()} SAS RIDEAU. {t('footer.allRights')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <button
                onClick={() => openLegalModal('mentions')}
                className="text-muted-foreground hover:text-accent text-sm transition-colors"
              >
                {t('footer.legal')}
              </button>
              <button
                onClick={() => openLegalModal('privacy')}
                className="text-muted-foreground hover:text-accent text-sm transition-colors"
              >
                {t('footer.privacy')}
              </button>
              <button
                onClick={() => openLegalModal('cookies')}
                className="text-muted-foreground hover:text-accent text-sm transition-colors"
              >
                {t('footer.cookies')}
              </button>
              <button
                onClick={handleManageCookies}
                className="text-muted-foreground hover:text-accent text-sm transition-colors"
              >
                {t('footer.manageCookies')}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {legalModalOpen && (
        <Suspense fallback={null}>
          <LegalModal
            open={legalModalOpen}
            onOpenChange={setLegalModalOpen}
            pageType={legalPageType}
          />
        </Suspense>
      )}

      {/* Cookie Banner - appears on first visit or when reopened via footer */}
      <CookieBanner
        onOpenCookiePolicy={handleOpenCookiePolicy}
        forceOpen={cookieBannerOpen}
        onClose={() => setCookieBannerOpen(false)}
      />
    </>
  );
};

export default Footer;
